import React, { useState, useEffect, useCallback } from "react";
import { Handshake, Search, Shield, Crown, CreditCard, Wallet, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../hooks/useAdminAuth";
import Pagination, { PAGE_SIZE } from "../components/admin/Pagination";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminUsers() {
  const navigate = useNavigate();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth("/api/ai/admin/users");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async (header) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users`, {
        headers: { Authorization: header },
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (authHeader) load(authHeader);
  }, [authHeader, load]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  useEffect(() => { setPage(1); }, [search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input
              type="text"
              autoComplete="username"
              placeholder="Admin username"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              style={styles.loginInput}
              autoFocus
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin password"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              style={styles.loginInput}
            />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button
              type="button"
              onClick={() => navigate("/submit-dispute")}
              style={styles.loginGhost}
            >Back to dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader active="users" onLogout={handleAdminLogout} />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>Users (admin)</h1>
            <p style={styles.subtitle}>
              {loading
                ? "Loading…"
                : `${filtered.length} of ${users.length} user${users.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <Search size={14} strokeWidth={2} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search username or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.search}
              />
            </div>
          </div>
        </div>

        {error && <div style={styles.errorBox}>Failed to load: {error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyTitle}>No users found</h3>
            <p style={styles.emptyText}>
              {users.length === 0 ? "No users have registered yet." : "No users match your search."}
            </p>
          </div>
        )}

        {filtered.length > 0 && (
          <div style={styles.list} role="table">
            <div style={styles.listHeader} role="row">
              <div style={{ ...styles.cell, ...styles.cellUser }}>User</div>
              <div style={{ ...styles.cell, ...styles.cellEmail }}>Email</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Disputes</div>
              <div style={{ ...styles.cell, ...styles.cellType }}>Type</div>
              <div style={{ ...styles.cell, ...styles.cellStripe }}>Stripe</div>
              <div style={{ ...styles.cell, ...styles.cellJoined }}>Joined</div>
              <div style={{ ...styles.cell }}>Edit</div>
            </div>

            {paginated.map((u) => (
              <div style={styles.row} role="row" key={u.id}>
                <div style={{ ...styles.cell, ...styles.cellUser }}>
                  <span style={styles.userName}>{u.username}</span>
                  <span style={styles.userId}>#{u.id}</span>
                </div>
                <div style={{ ...styles.cell, ...styles.cellEmail }}>
                  <span style={styles.email} title={u.email}>{u.email}</span>
                </div>
                <div style={{ ...styles.cell, ...styles.cellMeta }}>
                  <span style={styles.countPill}>{u.dispute_count}</span>
                </div>
                <div style={{ ...styles.cell, ...styles.cellType }}>
                  <div style={styles.tagRow}>
                    {u.is_admin && (
                      <span style={{ ...styles.tag, ...styles.tagAdmin }}>
                        <Crown size={11} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 3 }} />
                        Admin
                      </span>
                    )}
                    <span style={styles.tag}>{u.is_google_user ? "Google" : "Email"}</span>
                    {u.beta_user && <span style={{ ...styles.tag, ...styles.tagBeta }}>Beta</span>}
                    {!u.user_enabled && (
                      <span style={{ ...styles.tag, ...styles.tagPending }}>Pending</span>
                    )}
                  </div>
                </div>
                <div style={{ ...styles.cell, ...styles.cellStripe }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/stripe/user/${u.id}`)}
                    style={styles.stripeLink}
                    title="View this user's Stripe data"
                  >
                    {u.has_stripe_customer && (
                      <CreditCard size={13} strokeWidth={2} aria-label="Saved cards" />
                    )}
                    {u.has_connect_account && (
                      <Wallet size={13} strokeWidth={2} aria-label="Connect payout account" />
                    )}
                    <span style={styles.stripeLinkText}>View</span>
                  </button>
                </div>
                <div style={{ ...styles.cell, ...styles.cellJoined }}>
                  <span style={styles.joined}>{formatDate(u.created_at)}</span>
                </div>
                <div style={{ ...styles.cell }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/users/${u.id}/edit`)}
                    style={styles.stripeLink}
                    title="Edit this user"
                  >
                    <Pencil size={13} strokeWidth={2} aria-label="Edit user" />
                    <span style={styles.stripeLinkText}>Edit</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination page={safePage} total={filtered.length} onPageChange={setPage} />
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: {
    fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0",
    letterSpacing: "-0.3px", color: COLORS.ink,
  },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box",
    background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: {
    background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 6,
    padding: "8px 10px", fontSize: 12,
  },
  loginBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer",
  },
  loginGhost: {
    background: "transparent", color: COLORS.inkSoft,
    border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer",
  },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 1280, margin: "0 auto" },

  pageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 16, marginBottom: 24,
  },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },
  toolbar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  searchWrap: { position: "relative" },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: COLORS.inkMuted },
  search: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "8px 12px 8px 32px", fontSize: 13, fontFamily: SANS, color: COLORS.ink,
    outline: "none", minWidth: 260,
  },

  errorBox: {
    padding: 16, background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16,
  },
  emptyState: {
    textAlign: "center", padding: "60px 24px", background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
  },
  emptyTitle: { fontFamily: SERIF, fontSize: 22, margin: "0 0 8px 0", color: COLORS.ink },
  emptyText: { color: COLORS.inkMuted, margin: 0, fontSize: 13 },

  list: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, overflow: "hidden",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.8fr) 0.8fr 1.1fr 0.8fr 1fr 0.7fr",
    gap: 16, padding: "12px 20px",
    background: COLORS.bgAlt, color: COLORS.inkMuted,
    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.8fr) 0.8fr 1.1fr 0.8fr 1fr 0.7fr",
    gap: 16, padding: "16px 20px",
    background: COLORS.card, color: COLORS.ink, textAlign: "left",
    borderBottom: `1px solid ${COLORS.border}`,
    fontFamily: SANS, fontSize: 13, alignItems: "center", width: "100%",
  },
  cell: { minWidth: 0, display: "flex", alignItems: "center" },
  cellUser: { flexDirection: "column", alignItems: "flex-start", gap: 2 },
  cellEmail: {},
  cellMeta: {},
  cellType: {},
  cellStripe: {},
  cellJoined: { color: COLORS.inkMuted },

  userName: {
    fontWeight: 600, fontSize: 14, color: COLORS.ink,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
  },
  userId: { fontFamily: MONO, fontSize: 11, color: COLORS.inkMuted },
  email: {
    fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
  },
  countPill: {
    fontFamily: MONO, fontSize: 12, fontWeight: 600, color: COLORS.ink,
    background: COLORS.bgAlt, padding: "2px 8px", borderRadius: 6,
  },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  tag: {
    display: "inline-flex", alignItems: "center",
    fontSize: 11, fontWeight: 600, color: COLORS.inkSoft,
    background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
    padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
  },
  tagAdmin: { background: COLORS.dark, color: "#fff", borderColor: COLORS.dark },
  tagPending: { background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" },
  tagBeta: { background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" },
  iconTag: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: COLORS.inkSoft, background: COLORS.bgAlt,
    border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "3px 6px",
  },
  stripeLink: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "transparent", color: COLORS.inkSoft,
    border: `1px solid ${COLORS.border}`, borderRadius: 6,
    padding: "4px 10px", fontSize: 12, fontWeight: 600, fontFamily: SANS, cursor: "pointer",
  },
  stripeLinkText: { fontSize: 12 },
  dash: { color: COLORS.inkMuted },
  joined: { fontFamily: MONO, fontSize: 12 },
};
